import { IconAlertTriangle, IconExternalLink } from "@tabler/icons-react"
import { type FC } from "react"

import { isCourseIframeApplicationOrigin } from "@forento/core/utilities/courseIframe"

import { cn } from "../utilities/tailwind"

type Props = {
	url: string | null
	title: string
	compact?: boolean
	className?: string
	labels?: {
		unavailableTitle?: string
		unavailableDescription?: string
		fallbackDescription?: string
		openSource?: string
	}
}
const CourseIframe: FC<Props> = ({ url, title, compact = false, className, labels }) => {
	const renderUrl =
		url !== null && typeof window !== "undefined" && isCourseIframeApplicationOrigin(url, [window.location.origin])
			? null
			: url

	return (
		<div className={cn("w-full overflow-hidden rounded-xl border border-black/10 bg-black/2.5", className)}>
			<div className={cn("relative w-full", compact ? "h-80" : "h-96 sm:h-128 lg:h-160")}>
				{renderUrl === null ? (
					<ErrorState
						title={labels?.unavailableTitle ?? "This embed is unavailable"}
						description={
							labels?.unavailableDescription ?? "The source is missing, unsafe, or no longer approved."
						}
					/>
				) : (
					<iframe
						src={renderUrl}
						title={title}
						allow="fullscreen"
						allowFullScreen
						referrerPolicy="strict-origin-when-cross-origin"
						sandbox="allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
						className="size-full border-none bg-white"
					/>
				)}
			</div>
			{renderUrl !== null && (
				<div className="flex min-h-12 items-center justify-between gap-3 border-t border-black/10 px-4 py-2">
					<p className="text-sm opacity-70">
						{labels?.fallbackDescription ?? "Not seeing the content? The source may block embeds."}
					</p>
					<a
						href={renderUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="flex shrink-0 items-center gap-2 text-sm font-medium underline-offset-4 hover:underline"
					>
						{labels?.openSource ?? "Open in a new tab"}
						<IconExternalLink className="size-4" />
					</a>
				</div>
			)}
		</div>
	)
}

type ErrorStateProps = { title: string; description: string }
const ErrorState: FC<ErrorStateProps> = ({ title, description }) => (
	<div className="flex size-full items-center justify-center px-6 text-center">
		<div className="flex max-w-md flex-col items-center gap-3">
			<div className="flex size-11 items-center justify-center rounded-full bg-amber-500/10 text-amber-700">
				<IconAlertTriangle className="size-6" />
			</div>
			<div>
				<p className="font-semibold">{title}</p>
				<p className="mt-1 text-sm opacity-70">{description}</p>
			</div>
		</div>
	</div>
)

export default CourseIframe
